/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import * as SelectPrimitive from "@radix-ui/react-select";
import type { ComponentRef } from "react";
import * as React from "react";

import type { ThemeType } from "../../../../../global/emotion";
import {
  colors,
  fontSizes,
  lineHeights,
  numericPixels,
  radius,
  spacing,
  stroke,
} from "../../../../../global/emotion";
import { ChevronDownIcon } from "../../../_base/icons/Icon/NavigationIcons";

const triggerStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  backgroundColor: colors.transparent,
  borderColor: theme.semanticColors.neutralsLowest,
  borderRadius: radius.ms,
  borderStyle: "solid",
  borderWidth: stroke.regular,
  color: theme.semanticColors.primary,
  ":is([data-placeholder])": { color: theme.semanticColors.neutralsHigh },
  display: "flex",
  fontSize: fontSizes.bodyM,
  gap: spacing.xs,
  height: numericPixels[40],
  justifyContent: "space-between",
  lineHeight: lineHeights.bodyM,
  minHeight: numericPixels[64],
  outline: "none",
  padding: `${spacing.ms} ${spacing.m}`,
  transition: "border 200ms ease-in",
  width: spacing.full,
  ":hover:not(:disabled):not(:focus)": {
    borderColor: theme.semanticColors.neutralsVeryLow,
  },
  ":disabled": {
    color: theme.semanticColors.neutralsMedHigh,
    cursor: "not-allowed",
  },
  ":focus": {
    borderColor: theme.semanticColors.primary,
    outline: "none",
  },
});

type SelectTriggerProps = React.ComponentPropsWithoutRef<
  typeof SelectPrimitive.Trigger
>;

export const SelectTrigger = React.forwardRef<
  ComponentRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(({ children, ...props }, ref) => (
  <SelectPrimitive.Trigger ref={ref} css={triggerStyle} {...props}>
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDownIcon />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));

SelectTrigger.displayName = "SelectTrigger";
